
import Vue from "vue";
import { Auth } from "@/@core";
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore: 'could not find a declaration file for module' error
import { en, fr, nl } from "intl-tel-input/build/js/i18n";
import { FsegWorkerRestService } from "../services/FsegWorkerRestService";
import { worker } from "@/@modules/fseg/services/store/worker";
import { WorkerData } from "../services/interfaces/worker/WorkerData";
import intlTelInput, { Iti } from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.min.css";

type ItiLocale = {
  fr: any;
  nl: any;
};

export default Vue.extend({
  data() {
    const itiLocale: ItiLocale = { fr, nl };
    return {
      form: {
        eMail: worker.data?.email ?? "",
        mobilePhoneNumber: worker.data?.derogationAddress?.telephone ?? "",
        phoneNumber: worker.data?.address?.telephone ?? "",
      },
      fixPhoneRef: "fixPhoneField",
      isItiFixPhoneBooted: false,
      isItiMobilePhoneBooted: false,
      isLoading: false,
      isValid: false,
      itiFixPhone: null as Iti | null,
      itiFixPhoneDropdownId: "itiFixPhoneDropdown",
      itiFixPhoneEl: null as HTMLElement | null,
      itiFixPhoneId: "itiFixPhone",
      itiLocale,
      itiMobilePhone: null as Iti | null,
      itiMobilePhoneDropdownId: "itiMobilePhoneDropdown",
      itiMobilePhoneEl: null as HTMLElement | null,
      itiMobilePhoneId: "itiMobilePhone",
      mobilePhoneRef: "mobilePhoneField",
      window,
    };
  },
  destroyed() {
    // remove onCountrychange eventListeners
    this.itiFixPhoneEl?.removeEventListener("countrychange", this.itiFixPhoneCountrychangeListener);
    this.itiMobilePhoneEl?.removeEventListener("countrychange", this.itiMobilePhoneCountrychangeListener);
    // remove onOpenCountrydropdown eventListeners
    this.itiFixPhoneEl?.removeEventListener("open:countrydropdown", this.openCountrydropdownListener);
    this.itiMobilePhoneEl?.removeEventListener("open:countrydropdown", this.openCountrydropdownListener);

    // destory iti instances
    this.itiFixPhone?.destroy();
    this.itiMobilePhone?.destroy();
  },
  async mounted() {
    let defaultCountry = "BE";
    if (!this.form.phoneNumber || !this.form.mobilePhoneNumber) {
      try {
        defaultCountry = (await (await fetch("https://ipapi.co/json")).json()).country_code;
      } catch (error) {
        console.error("ipapi", error);
      }
    }

    // launch a 1st form validation to allow error messages to be displayed
    (this.$refs.form as Vue & { validate: () => void }).validate();

    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore: 'could not find a declaration file for module' error
    const itiUtils = import("intl-tel-input/build/js/utils");

    this.itiFixPhoneEl = document.getElementById(this.itiFixPhoneId);
    this.itiMobilePhoneEl = document.getElementById(this.itiMobilePhoneId);

    if (this.itiFixPhoneEl) {
      this.itiFixPhone = this.initIti({
        countrychangeListener: this.itiFixPhoneCountrychangeListener,
        defaultCountry,
        dropdownId: this.itiFixPhoneDropdownId,
        fieldRef: this.fixPhoneRef,
        initialNb: this.form.phoneNumber,
        inputEl: this.itiFixPhoneEl,
        isBootedVar: "isItiFixPhoneBooted",
        itiUtils,
        numberType: "FIXED_LINE",
      });
    }

    if (this.itiMobilePhoneEl) {
      this.itiMobilePhone = this.initIti({
        countrychangeListener: this.itiMobilePhoneCountrychangeListener,
        defaultCountry,
        dropdownId: this.itiMobilePhoneDropdownId,
        fieldRef: this.mobilePhoneRef,
        initialNb: this.form.mobilePhoneNumber,
        inputEl: this.itiMobilePhoneEl,
        isBootedVar: "isItiMobilePhoneBooted",
        itiUtils,
        numberType: "MOBILE",
      });
    }
  },
  computed: {
    emailRule(): (v: string) => string | true {
      return v => !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || this.$tc("fseg.error.email");
    },
    fixPhoneHint(): string | undefined {
      return !!this.form.phoneNumber && !!this.itiFixPhone?.isValidNumber()
        ? `🗸 ${this.itiFixPhone?.getNumber()}`
        : undefined;
    },
    itiFixPhoneRule(): (v: string) => string | true {
      return v =>
        !v ||
        (this.isItiFixPhoneBooted && !this.itiFixPhone!.isValidNumber()
          ? this.$tc(`fseg.error.${this.getErrorStr(this.itiFixPhone!, "fix")}`)
          : true);
    },
    itiMobilePhoneRule(): (v: string) => string | true {
      return v =>
        !v ||
        (this.isItiMobilePhoneBooted && !this.itiMobilePhone!.isValidNumber()
          ? this.$tc(`fseg.error.${this.getErrorStr(this.itiMobilePhone!, "mobile")}`)
          : true);
    },
    mobilePhoneHint(): string | undefined {
      return !!this.form.mobilePhoneNumber && !!this.itiMobilePhone?.isValidNumber()
        ? `🗸 ${this.itiMobilePhone?.getNumber()}`
        : undefined;
    },
  },
  methods: {
    getErrorStr(iti: Iti, errName: "fix" | "mobile") {
      const error = iti.getValidationError();
      return [0, 1, 2, 3, 4, 5].includes(error) ? `iti.${error === 1 ? "countryCode" : errName}` : "unknown";
    },
    async edit() {
      if (this.itiMobilePhone && this.itiFixPhone && worker.data) {
        try {
          this.isLoading = true;

          const workerContactData = await FsegWorkerRestService.editContactData(await Auth.getClaim("ssin"), {
            eMail: this.form.eMail,
            mobilePhoneNumber: this.itiMobilePhone.getNumber(),
            phoneNumber: this.itiFixPhone.getNumber(),
          });

          worker.data.email = workerContactData.eMail;
          worker.data.derogationAddress
            ? (worker.data.derogationAddress.telephone = workerContactData.mobilePhoneNumber)
            : (worker.data.derogationAddress = {
                telephone: workerContactData.mobilePhoneNumber,
              } as WorkerData["derogationAddress"]);
          worker.data.address
            ? (worker.data.address.telephone = workerContactData.phoneNumber)
            : (worker.data.address = {
                telephone: workerContactData.phoneNumber,
              } as WorkerData["address"]);

          this.$modal.close(true);
        } catch (error) {
          this.$snackbar.show(`${(error as any).message}`, 2000, undefined, { error: true });
        }
      }
    },
    initIti({
      countrychangeListener,
      defaultCountry,
      dropdownId,
      fieldRef,
      initialNb,
      inputEl,
      isBootedVar,
      itiUtils,
      numberType,
    }: {
      countrychangeListener: () => void;
      defaultCountry: string;
      dropdownId: string;
      fieldRef: string;
      initialNb: string;
      inputEl: HTMLElement;
      isBootedVar: "isItiFixPhoneBooted" | "isItiMobilePhoneBooted";
      itiUtils: Promise<any>;
      numberType: "FIXED_LINE" | "MOBILE";
    }) {
      const iti = intlTelInput(inputEl as HTMLInputElement, {
        containerClass: "iti-full",
        dropdownContainer: document.getElementById(dropdownId) as HTMLElement,
        i18n: this.isKeyOfItiLocale(this.$i18n.locale) ? this.itiLocale[this.$i18n.locale] : en,
        initialCountry: !initialNb ? defaultCountry : initialNb.startsWith("+") ? "auto" : "",
        loadUtils: () => itiUtils,
        placeholderNumberType: numberType,
        separateDialCode: true,
        strictMode: true,
        validationNumberTypes: [numberType],
      });

      // setNumber to remove (visually) country code (given that separateDialCode === true)
      iti.setNumber(initialNb);

      // launch fields validation when iti is initialized
      iti.promise.then(() => {
        this[isBootedVar] = true;
        (this.$refs[fieldRef] as Vue & { validate: () => void }).validate();
      });

      // addEventListener to launch fields validation onCountrychange
      inputEl.addEventListener("countrychange", countrychangeListener);

      // addEventListener to prevent a bug
      inputEl.addEventListener("open:countrydropdown", this.openCountrydropdownListener);

      return iti;
    },
    itiFixPhoneCountrychangeListener() {
      (this.$refs[this.fixPhoneRef] as Vue & { validate: () => void }).validate();
    },
    itiMobilePhoneCountrychangeListener() {
      (this.$refs[this.mobilePhoneRef] as Vue & { validate: () => void }).validate();
    },
    isKeyOfItiLocale(locale: string): locale is keyof ItiLocale {
      return locale in this.itiLocale;
    },
    openCountrydropdownListener() {
      // prevent a bug that appears when:
      //   1. user manually opens dropdown (by clicking on flag or dial code)
      //      => 'iti__search-input' is not focused
      //   2. user directly types "+" then selects a country
      // !!! BUG => dropodown is no longer displayed when flag or dial code is clicked
      setTimeout(() => {
        (document.querySelector(".iti__search-input") as HTMLInputElement).focus();
      }, 1);
    },
  },
});
